import React, { useEffect, useState } from 'react';
import GalleryCard from './GalleryCard';
import { useDarkMode } from '../contexts/DarkModeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import ShowMoreButton from './ShowMoreButton';
import { API_BASE_URL } from '../data/objects/gameColor';

interface GalleryGroupProps {
    zipName: string;
    galleryPath: string;
}

interface SubfolderMedia {
    [subfolderName: string]: string[];
}

interface GalleryMediaEntry {
    fullFolderName: string;
    media: SubfolderMedia;
}

interface GalleryMedia {
    [displayFolderName: string]: GalleryMediaEntry;
}

const GalleryGroup: React.FC<GalleryGroupProps> = ({ galleryPath, zipName }) => {
    const [galleryMedia, setGalleryMedia] = useState<GalleryMedia>({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [currentFolder, setCurrentFolder] = useState('Main');
    const [selectedSubfolder, setSelectedSubfolder] = useState<{ [mainFolder: string]: string }>({});
    const [showAll, setShowAll] = useState(false);
    const { isDarkMode } = useDarkMode();

    const mediaPerPage = 12;
    const maxCondensedImages = 6;
    const [pagination, setPagination] = useState<{ [key: string]: number }>({});
    const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 992);

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/list-gallery?path=${galleryPath}`)
            .then(response => response.json())
            .then(data => {
                const keys = Object.keys(data);

                const isFlatStructure = keys.every(key => {
                    const entry = data[key];
                    return entry.media && Object.keys(entry.media).length === 1 && Object.keys(entry.media)[0] === 'Main';
                });

                if (isFlatStructure) {
                    // Merge all entries into one "flat gallery"
                    const mergedEntry: GalleryMediaEntry = {
                        fullFolderName: 'FlatRootGallery',
                        media: {}
                    };

                    keys.forEach(key => {
                        const displayName = key;
                        const entry = data[key];
                        const images = entry.media['Main'] || [];
                        if (images.length > 0) {
                            mergedEntry.media[displayName] = images;
                        }
                    });

                    data = {
                        'Gallery': mergedEntry
                    };
                }

                setGalleryMedia(data);

                const initialPagination = Object.keys(data).reduce((acc, folder) => {
                    const subfolders = Object.keys(data[folder].media);
                    subfolders.forEach(sub => {
                        acc[`${folder}-${sub}`] = 1;
                    });
                    return acc;
                }, {} as { [key: string]: number });

                setPagination(initialPagination);
            })

        setShowAll(false);

    }, [galleryPath]);

    useEffect(() => {
        const handleResize = () => setIsLargeScreen(window.innerWidth >= 992);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getActiveMediaArray = (folderOverride?: string): string[] => {
        const folder = folderOverride || currentFolder;
        const folderEntry = galleryMedia[folder];
        if (!folderEntry) return [];

        const subfolderName = selectedSubfolder[folder] || 'Main';
        return folderEntry.media[subfolderName] || [];
    };

    const handleMediaClick = (mediaUrl: string, folder: string) => {
        const folderEntry = galleryMedia[folder];
        if (!folderEntry) return;

        let subfolderName = selectedSubfolder[folder];

        // If no subfolder is set yet, find one containing an image
        if (!subfolderName) {
            subfolderName = Object.keys(folderEntry.media).find(sub =>
                folderEntry.media[sub]?.includes(mediaUrl)
            ) || 'Main';

            setSelectedSubfolder(prev => ({
                ...prev,
                [folder]: subfolderName
            }));
        }

        const folderMedia = folderEntry.media[subfolderName] || [];
        const relativeIndex = folderMedia.findIndex(url => url === mediaUrl);

        setCurrentFolder(folder);
        setCurrentMediaIndex(relativeIndex);
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);
    const showNextMedia = () => {
        const currentMedia = getActiveMediaArray();
        setCurrentMediaIndex(prev => (prev + 1) % currentMedia.length);
    };
    const showPreviousMedia = () => {
        const currentMedia = getActiveMediaArray();
        setCurrentMediaIndex(prev => (prev - 1 + currentMedia.length) % currentMedia.length);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowRight') showNextMedia();
        else if (e.key === 'ArrowLeft') showPreviousMedia();
        else if (e.key === 'Escape') closeModal();
    };

    useEffect(() => {
        if (isModalOpen) {
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
    }, [isModalOpen]);

    const handleDownloadAll = () => {
        const downloadUrl = `${API_BASE_URL}/api/download-gallery?path=${galleryPath}&galleryName=${zipName}`;
        window.open(downloadUrl, '_blank');
    };

    const toggleShowAll = () => setShowAll(prev => !prev);
    const isVideo = (file: string) => /\.(mp4|mov|avi)$/i.test(file);

    const hasSubfolders = Object.values(galleryMedia).some(entry => {
        const subfolders = Object.keys(entry.media);
        return subfolders.length > 1 || (subfolders.length === 1 && subfolders[0] !== 'Main');
    });

    const getCondensedMedia = (): { folder: string; url: string }[] => {
        if (Object.keys(galleryMedia).length === 0) return [];
        const mediaWithFolders: { folder: string; url: string }[] = [];

        for (const [folder, { media }] of Object.entries(galleryMedia)) {
            const subfolder = Object.keys(media)[0];
            const subfolderMedia = media[subfolder] || [];
            const selectedMedia = subfolderMedia.slice(0, maxCondensedImages - mediaWithFolders.length);
            mediaWithFolders.push(...selectedMedia.map(url => ({ folder, url })));
            if (mediaWithFolders.length >= maxCondensedImages) break;
        }

        return mediaWithFolders;
    };

    const getInitialSubfolder = (folder: string) => {
        const folderEntry = galleryMedia[folder];
        if (!folderEntry) return 'Main';
        if (folderEntry.media['Main'] && folderEntry.media['Main'].length > 0) return 'Main';
        const available = Object.entries(folderEntry.media).find(([sub, files]) => sub !== 'Main' && files.length > 0);
        return available ? available[0] : 'Main';
    };

    const handleSubfolderDownload = (fullFolderName: string) => {
        const folderPath = `${galleryPath}/${fullFolderName}`;
        const downloadUrl = `${API_BASE_URL}/api/download-gallery?path=${folderPath}&galleryName=${zipName}-${fullFolderName}`;
        window.open(downloadUrl, '_blank');
    };
    
    const allMediaCount = Object.values(galleryMedia).flatMap(entry => Object.values(entry.media).flat()).length;
    const condensedMedia = getCondensedMedia();
    const shouldShowToggleButton = (showAll || allMediaCount > condensedMedia.length) || hasSubfolders;
    const hasGallery = allMediaCount > 0;

    return (
        <>
            {hasGallery && (
                <div className="mt-5">
                    <div className="row mb-2">
                        <h1 className="col kanit-extrabold">Gallery</h1>
                        <div className="col-auto my-auto">
                            <button onClick={handleDownloadAll} className="btn btn-primary direct-link kanit-regular">
                                <FontAwesomeIcon icon={faDownload} className="me-2 fa-xl" />
                                Download Gallery
                            </button>
                        </div>
                    </div>

                    <div className={`${showAll && hasSubfolders ? `card release-info-container ${isDarkMode ? 'dark-mode-3' : 'light-mode-3'}` : ''}`}>
                        {showAll || !hasSubfolders ? (
                            Object.entries(galleryMedia).map(([displayName, { fullFolderName, media }], index) => {
                                const availableSubfolders = Object.keys(media);
                                const currentSubfolder = selectedSubfolder[displayName] || getInitialSubfolder(displayName);
                                const subfolderMedia = media[currentSubfolder] || [];

                                const currentPage = pagination[`${displayName}-${currentSubfolder}`] || 1;
                                const indexOfLastMedia = currentPage * mediaPerPage;
                                const indexOfFirstMedia = indexOfLastMedia - mediaPerPage;
                                const currentMediaDisplayed = subfolderMedia.slice(indexOfFirstMedia, indexOfLastMedia);
                                const totalPages = Math.ceil(subfolderMedia.length / mediaPerPage);

                                const handlePageClick = (pageNumber: number) => setPagination(prev => ({ ...prev, [`${displayName}-${currentSubfolder}`]: pageNumber }));
                                const handleNextPage = () => { if (currentPage < totalPages) setPagination(prev => ({ ...prev, [`${displayName}-${currentSubfolder}`]: currentPage + 1 })); };
                                const handlePrevPage = () => { if (currentPage > 1) setPagination(prev => ({ ...prev, [`${displayName}-${currentSubfolder}`]: currentPage - 1 })); };

                                return (
                                    <div key={displayName}>

                                        {displayName !== 'Gallery' && Object.keys(media).some(key => key !== 'Main') && (
                                            <h3 className={index === 0 ? 'mt-2' : 'mt-5'}>{displayName}</h3>
                                        )}

                                        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-md-between">
                                            {availableSubfolders.length > 0 && availableSubfolders[0].toString() !== 'Main' && (
                                                <div className="mb-3">
                                                    {availableSubfolders.map(sub => (
                                                        <button
                                                            key={sub}
                                                            className={`btn ${currentSubfolder === sub ? 'btn-primary' : 'btn-dark'} btn-sm mx-1`}
                                                            onClick={() => {
                                                                setSelectedSubfolder(prev => ({ ...prev, [displayName]: sub }));
                                                                setPagination(prev => ({ ...prev, [`${displayName}-${sub}`]: 1 }));
                                                            }}
                                                        >
                                                            {sub}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {displayName !== 'Gallery' && Object.keys(media).some(key => key !== 'Main') && (
                                                <div className="mb-3">
                                                    <button onClick={() => handleSubfolderDownload(fullFolderName)} className="btn btn-secondary direct-link kanit-regular">
                                                        <FontAwesomeIcon icon={faDownload} className="me-2 fa-xl" />
                                                        Download {displayName} Gallery
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className={`card row release-info-container ${!hasSubfolders ? 'dark-mode-3' : 'dark-mode-1'}`}> {/*${(isDarkMode) ? 'dark-mode-1' : 'light-mode-2'}*/}
                                            {currentMediaDisplayed.map((mediaUrl, index) => (
                                                <GalleryCard
                                                    key={index}
                                                    mediaUrl={mediaUrl}
                                                    handleClick={() => handleMediaClick(mediaUrl, displayName)}
                                                    isVideo={isVideo(mediaUrl)}
                                                />
                                            ))}

                                            {totalPages > 1 && (
                                                <nav className="mt-4" aria-label={`Page navigation for ${displayName}`}>
                                                    <ul className="pagination justify-content-center">
                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                            <a className="page-link" onClick={handlePrevPage} aria-label="Previous">
                                                                <span aria-hidden="true">&laquo;</span>
                                                            </a>
                                                        </li>
                                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                            .filter(pageNumber => isLargeScreen || pageNumber === 1 || pageNumber === totalPages || (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1))
                                                            .map(pageNumber => (
                                                                <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                                                    <a className="page-link" onClick={() => handlePageClick(pageNumber)}>{pageNumber}</a>
                                                                </li>
                                                            ))}
                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                            <a className="page-link" onClick={handleNextPage} aria-label="Next">
                                                                <span aria-hidden="true">&raquo;</span>
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={`card row release-info-container ${isDarkMode ? 'dark-mode-3' : 'light-mode-3'}`}>
                                {condensedMedia.map(({ folder, url }, index) => (
                                    <GalleryCard
                                        key={index}
                                        mediaUrl={url}
                                        handleClick={() => handleMediaClick(url, folder)}
                                        isVideo={isVideo(url)}
                                    />
                                ))}
                                {shouldShowToggleButton && (
                                    <ShowMoreButton showAll={showAll} toggleShowAll={toggleShowAll} />
                                )}
                            </div>
                        )}

                        {shouldShowToggleButton && showAll && hasSubfolders && (
                            <ShowMoreButton showAll={showAll} toggleShowAll={toggleShowAll} />
                        )}
                    </div>

                    {isModalOpen && (
                        <div>
                            <button className="modal-arrow left-arrow" onClick={showPreviousMedia}>&#10094;</button>
                            <div className="custom-modal-overlay" onClick={closeModal}>
                                <div className="custom-modal show" onClick={(e) => e.stopPropagation()}>
                                    <div className={`modal-media-container ${isDarkMode ? 'dark-mode-gallery-modal' : 'light-mode-2'}`}>
                                        {isVideo(getActiveMediaArray()[currentMediaIndex]) ? (
                                            <video src={getActiveMediaArray()[currentMediaIndex]} controls className="custom-modal-video-media" autoPlay />
                                        ) : (
                                            <img src={getActiveMediaArray()[currentMediaIndex]} alt={`Gallery ${currentMediaIndex + 1}`} className="custom-modal-image-media rounded" onClick={(e) => { e.stopPropagation(); window.open(getActiveMediaArray()[currentMediaIndex], '_blank'); }} />
                                        )}
                                    </div>
                                    <div className="media-counter">{`${currentMediaIndex + 1}/${getActiveMediaArray()?.length || 0}`}</div>
                                </div>
                            </div>
                            <button className="modal-arrow right-arrow" onClick={showNextMedia}>&#10095;</button>
                            <button className="close" onClick={closeModal}>&times;</button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default GalleryGroup;